import type React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { type Publisher, publisherApi } from '../lib/api'

interface AuthContextType {
  publisher: Publisher | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isLoading: boolean
  error: string | null
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [publisher, setPublisher] = useState<Publisher | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const logout = () => {
    setPublisher(null)
    setToken(null)
    localStorage.removeItem('publisherToken')
    localStorage.removeItem('publisher')
  }

  useEffect(() => {
    const storedToken = localStorage.getItem('publisherToken')
    const storedPublisher = localStorage.getItem('publisher')

    if (storedToken && storedPublisher) {
      setToken(storedToken)
      setPublisher(JSON.parse(storedPublisher))

      // In demo mode, skip profile fetch
      if (!storedToken.startsWith('demo-token')) {
        publisherApi
          .getProfile()
          .then((data) => {
            setPublisher(data)
            localStorage.setItem('publisher', JSON.stringify(data))
          })
          .catch(() => {
            logout()
          })
      }
    }
    setIsLoading(false)
  }, [])

  const login = async (email: string, password: string) => {
    setError(null)
    setIsLoading(true)

    try {
      // Demo mode for testing
      if (window.location.hostname === 'localhost' || window.location.hostname.includes('pages.dev')) {
        const demoPublisher = {
          id: 'demo-user',
          email: email,
          username: email.split('@')[0],
          status: 'active' as const,
          tier: 'starter' as const,
          totalEarnings: 1250.50,
          pendingPayment: 450.00,
          createdAt: new Date().toISOString()
        }
        const demoToken = 'demo-token-' + Date.now()
        
        setPublisher(demoPublisher)
        setToken(demoToken)
        
        localStorage.setItem('publisherToken', demoToken)
        localStorage.setItem('publisher', JSON.stringify(demoPublisher))
      } else {
        const { publisher, token } = await publisherApi.login(email, password)

        setPublisher(publisher)
        setToken(token)

        localStorage.setItem('publisherToken', token)
        localStorage.setItem('publisher', JSON.stringify(publisher))
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed')
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        publisher,
        token,
        login,
        logout,
        isLoading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
